import type { ClientContactOutcome, ClientContactType } from "@athena/common/graphql/generated/schema";
import type { ClientContactFieldsFragment } from "@/graphql/clients.generated";
import type {
    ClientDischargeType,
    ClientDoNotContactReason,
    ClientWelcomeSkipReason,
} from "@athena/common/graphql/generated/schema";
import type {
    ClientAccountFieldsFragment,
    ClientFieldsFragment,
    CoordinatorNoteFieldsFragment,
} from "@/graphql/clients.generated";
import { ALUMNI_ROOT } from "@/lib/adminPilot";
import { mockLifeStatusFor } from "./mockLifeStatus";

/** The two halves of an alum's full record, each its own URL so it can be linked to and refreshed. */
export const RECORD_TABS = [
    "contacts",
    "notes",
] as const;
export type RecordTab = (typeof RECORD_TABS)[number];

export const isRecordTab = (value: string | undefined): value is RecordTab =>
    (RECORD_TABS as readonly string[]).includes(value ?? "");

/** An alum's profile. */
export const alumnusPath = (alumnusId: string) => `${ALUMNI_ROOT}/${alumnusId}`;

/** One tab of an alum's full record. */
export const recordPath = (alumnusId: string, tab: RecordTab) => `${alumnusPath(alumnusId)}/record/${tab}`;

/**
 * The alumni coordinator console's view of a client (`ClientRecord`).
 *
 * Identity and program details come from the `Client` API (`toClientRecord`). Employment, housing and
 * program have no API yet and are stand-ins from `mockLifeStatus.ts`. Contacts and notes have no
 * API either: they start empty and live in the page's own state, gone on reload.
 */

/**
 * Where a client stands with outreach, as the coordinator sees it on the badge. Not the API's
 * `outreachStatus`: that is only the stored decision (see `CONTACT_STATUS_FOR_OUTREACH_STATUS`).
 */
export type ContactStatus = "NEEDS_OUTREACH" | "ENGAGED" | "LOST_CONTACT" | "DO_NOT_CONTACT";

/**
 * Engagement level → `StatusBadge` variant. Warn, success and destructive, per the pilot brief; do
 * not contact is critical, a red deeper than lost contact's (ATH-231), so nobody misses it
 * before picking up the phone.
 */
export const CONTACT_STATUS_VARIANT = {
    NEEDS_OUTREACH: "warning",
    ENGAGED: "success",
    LOST_CONTACT: "destructive",
    DO_NOT_CONTACT: "critical",
} as const satisfies Record<ContactStatus, "warning" | "success" | "destructive" | "critical">;

/** In the order the log-contact dialog offers them. */
export const CONTACT_CHANNELS = [
    "call",
    "text",
    "virtual",
    "email",
    "letter",
    "inPerson",
] as const;
/**
 * `chat` is the in-app chat. The API records it as a contact type, so history can show one, but it
 * is not offered as a choice: a chat is logged where it happens, not by hand.
 */
export type ContactChannel = (typeof CONTACT_CHANNELS)[number] | "chat";

/** The API's name for each channel. */
export const CONTACT_TYPE_FOR_CHANNEL = {
    call: "PHONE_CALL",
    text: "TEXT_MESSAGE",
    virtual: "VIRTUAL_MEETING",
    email: "EMAIL",
    letter: "LETTER",
    inPerson: "IN_PERSON",
    chat: "CHAT",
} as const satisfies Record<ContactChannel, ClientContactType>;

/** The reverse of `CONTACT_TYPE_FOR_CHANNEL`, derived so the two cannot disagree. */
export const CHANNEL_FOR_CONTACT_TYPE = Object.fromEntries(
    Object.entries(CONTACT_TYPE_FOR_CHANNEL).map(
        ([
            channel,
            type,
        ]) => [
            type,
            channel,
        ],
    ),
) as Record<ClientContactType, ContactChannel>;

/** How a contact attempt went, in the order the log-contact dialog offers them. */
export const CONTACT_OUTCOMES = [
    "reached",
    "leftMessage",
    "noAnswer",
    "wrongNumber",
    "declined",
] as const;
export type ContactOutcome = (typeof CONTACT_OUTCOMES)[number];

/** The API's name for each outcome. */
export const API_OUTCOME_FOR_OUTCOME = {
    reached: "REACHED",
    leftMessage: "LEFT_MESSAGE",
    noAnswer: "NO_ANSWER",
    wrongNumber: "WRONG_NUMBER",
    declined: "DECLINED",
} as const satisfies Record<ContactOutcome, ClientContactOutcome>;

const OUTCOME_FOR_API_OUTCOME = Object.fromEntries(
    Object.entries(API_OUTCOME_FOR_OUTCOME).map(
        ([
            outcome,
            apiOutcome,
        ]) => [
            apiOutcome,
            outcome,
        ],
    ),
) as Record<ClientContactOutcome, ContactOutcome>;

/**
 * Only a conversation counts as reaching someone. A wrong number is red: the number on file is no
 * good, and nobody can be reached on it until it is replaced. A declined contact is the other one to
 * notice — they chose not to talk — so it is amber; a voicemail or no answer is a neutral attempt
 * and reads as muted.
 */
export const CONTACT_OUTCOME_VARIANT = {
    reached: "success",
    leftMessage: "muted",
    noAnswer: "muted",
    wrongNumber: "destructive",
    declined: "warning",
} as const satisfies Record<ContactOutcome, "success" | "warning" | "destructive" | "muted">;

export interface EmergencyContact {
    name: string;
    relationship?: string;
    phone?: string;
}

export interface ContactEntry {
    id: string;
    channel: ContactChannel;
    outcome: ContactOutcome;
    /** ISO 8601 date-time. */
    contactedAt: string;
    /** ISO 8601 date-time: when it was logged, which may be later than when it happened. */
    loggedAt: string;
    /** Whether the viewer may edit or delete it — the server decides (who logged it, or platform staff). */
    canEdit: boolean;
    note?: string;
    /** `YYYY-MM-DD`: when the coordinator set the next contact for. Unset for no follow-up. */
    followUpDate?: string;
    contactedBy: string;
}

export interface CoordinatorNote {
    id: string;
    text: string;
    /** The author's name, else their email. Null once the coordinator who wrote it has been removed. */
    author: string | null;
    /** Whether the viewer may edit or delete it — the server decides (its author, or platform staff). */
    canEdit: boolean;
    /** ISO 8601 date-time of the last edit; unset if the note has never been edited. */
    editedAt?: string;
    /** ISO 8601 date-time. */
    createdAt: string;
}

export interface ClientRecord {
    /** The alum's client id: the same whether or not they have an athena account. */
    id: string;
    firstName: string;
    lastName: string;
    phone?: string;
    /** Unset when neither their account nor KIPU has one. */
    email?: string;
    location?: string;
    /** Transitional Living, IOP…, as entered when they were imported. */
    program?: string;
    coordinator?: string;
    /** Stand-ins until the API carries them — see `mockLifeStatus.ts`. */
    employment?: string;
    housing?: string;
    address?: string;
    sex?: string;
    genderIdentity?: string;
    /** From KIPU. Unset when the import sent none, or while still in the program. */
    dischargeType?: ClientDischargeType;
    /** Calendar dates, `YYYY-MM-DD`, read as local days — a sober date has no time zone. */
    soberDate?: string;
    /** Unset while still in the program. */
    dischargeDate?: string;
    birthday?: string;
    status: ContactStatus;
    /** Why outreach was stopped, in the coordinator's words. Unset while still in outreach. */
    statusReason?: string;
    /** Why they are do not contact. Unset for any other status. */
    doNotContactReason?: ClientDoNotContactReason;
    /** When their welcome email was last sent (ATH-244). Unset: never. */
    welcomeEmailSentAt?: string;
    /** Why a welcome email cannot be sent now; unset when it can. */
    welcomeEmailBlocker?: ClientWelcomeSkipReason;
    /** Six check-in intervals without reaching them: offer the coordinator "Mark lost contact". */
    lostContactSuggested: boolean;
    /** ISO 8601 date-time of the latest logged contact. Unset when never contacted, or not loaded. */
    lastContactedAt?: string;
    /** `YYYY-MM-DD`. Unset when nothing is scheduled. */
    nextFollowUp?: string;
    emergencyContact?: EmergencyContact;
    /** Their athena account. Unset when they have none, or when the query did not load it. */
    account?: ClientAccount;
    contacts: ContactEntry[];
}

/**
 * The contact details kept on an alum's account (ATH-244): where to reach them when it is not what
 * they sign in with. Unset ones fall back to the sign-in email and phone.
 */
export interface ClientAccount {
    userId: string;
    /** What they sign in with, and where emails go while no contact email is set. */
    signInEmail: string;
    contactEmail?: string;
    contactPhone?: string;
}

export const toClientAccount = (user: ClientAccountFieldsFragment | null | undefined): ClientAccount | undefined =>
    user
        ? {
              userId: user.id,
              signInEmail: user.email,
              contactEmail: present(user.contactEmail),
              contactPhone: present(user.contactPhoneNumber),
          }
        : undefined;

/** How many contacts the profile shows before "Show more". */
export const RECENT_CONTACT_LIMIT = 10;

const MS_PER_DAY = 86_400_000;

/** A `YYYY-MM-DD` calendar date as local midnight, not UTC — `new Date("2026-03-04")` is UTC. */
export const parseCalendarDate = (value: string): Date => {
    const [
        year = 0,
        month = 1,
        day = 1,
    ] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
};

export const toCalendarDate = (date: Date): string =>
    [
        String(date.getFullYear()),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
    ].join("-");

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Whole days from `value` to `today`. Rounded so a daylight-saving hour cannot drop a day. */
export const daysSince = (value: string, today: Date): number =>
    Math.round((startOfDay(today).getTime() - parseCalendarDate(value).getTime()) / MS_PER_DAY);

export interface Duration {
    years: number;
    months: number;
    days: number;
}

/** Calendar years, months and days from `value` to `today` — "7 mo 21 d", not "233 days". */
export const durationSince = (value: string, today: Date): Duration => {
    const start = parseCalendarDate(value);
    let years = today.getFullYear() - start.getFullYear();
    let months = today.getMonth() - start.getMonth();
    let days = today.getDate() - start.getDate();
    if (days < 0) {
        months -= 1;
        // Days in the month before `today`'s — day 0 of a month is the last day of the previous one.
        days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    }
    if (months < 0) {
        years -= 1;
        months += 12;
    }
    return { years, months, days };
};

export const addDays = (date: Date, days: number): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export type FollowUpChoice = "none" | "oneWeek" | "twoWeeks" | "oneMonth" | "ninetyDays" | "custom";

/**
 * The dated quick picks, the same in both dialogs so a coordinator learns one set. Logging a
 * contact puts "none" in front, since a contact need not lead to another.
 */
export const FOLLOW_UP_DATE_CHOICES = [
    "oneWeek",
    "twoWeeks",
    "oneMonth",
    "ninetyDays",
    "custom",
] as const satisfies readonly FollowUpChoice[];

/** Days ahead each quick pick lands. "1 month" is 30 days, as in the mockups, not a calendar month. */
const FOLLOW_UP_OFFSET_DAYS = {
    oneWeek: 7,
    twoWeeks: 14,
    oneMonth: 30,
    ninetyDays: 90,
} as const satisfies Record<Exclude<FollowUpChoice, "none" | "custom">, number>;

/** The date a follow-up choice means, or `undefined` for none — and for a custom pick not yet made. */
export const resolveFollowUp = (
    choice: FollowUpChoice,
    customDate: Date | undefined,
    today: Date,
): Date | undefined => {
    if (choice === "none") {
        return undefined;
    }
    if (choice === "custom") {
        return customDate;
    }
    return addDays(today, FOLLOW_UP_OFFSET_DAYS[choice]);
};

/** Newest first, so the first `RECENT_CONTACT_LIMIT` are the recent ones. */
export const sortContactsNewestFirst = (contacts: ContactEntry[]): ContactEntry[] =>
    [...contacts].sort((a, b) => b.contactedAt.localeCompare(a.contactedAt));

const DAY_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const NUMERIC_DAY_FORMAT = new Intl.DateTimeFormat("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
const DAY_TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
});

/** "Jul 30, 1989". */
export const formatDay = (date: Date): string => DAY_FORMAT.format(date);
/** "09/25/2026" — the picked date in a date field. */
export const formatNumericDay = (date: Date): string => NUMERIC_DAY_FORMAT.format(date);
export const formatCalendarDate = (value: string): string => formatDay(parseCalendarDate(value));
const CLOCK_TIME_FORMAT = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });
/** "2:14 PM", in the reader's time zone: a contact's time where its day is shown above it. */
export const formatClockTime = (value: string): string => CLOCK_TIME_FORMAT.format(new Date(value));
/** "Jul 23, 2026, 2:14 PM", in the reader's time zone. */
export const formatContactTime = (value: string): string => DAY_TIME_FORMAT.format(new Date(value));

export const fullName = (client: Pick<ClientRecord, "firstName" | "lastName">): string =>
    `${client.firstName} ${client.lastName}`;

/**
 * The API's `outreachStatus` → the `ContactStatus` a coordinator sees.
 *
 * They differ because the API stores only what a coordinator decided — keep reaching out (ACTIVE),
 * or stop (LOST_CONTACT, DO_NOT_CONTACT). A stopped client shows that decision as-is. An ACTIVE one
 * is shown as needs outreach or engaged, and which of the two depends on their last contact, so it
 * is derived and never stored: the API's `engagementStatus`, read in `toClientRecord`. This map is
 * only the fallback for an ACTIVE client the API sent no engagement status for.
 */
const CONTACT_STATUS_FOR_OUTREACH_STATUS = {
    ACTIVE: "NEEDS_OUTREACH",
    LOST_CONTACT: "LOST_CONTACT",
    DO_NOT_CONTACT: "DO_NOT_CONTACT",
} as const satisfies Record<ClientFieldsFragment["outreachStatus"], ContactStatus>;

const present = (value: string | null | undefined): string | undefined => value || undefined;

/**
 * One `Client` from the API as the `ClientRecord` the directory and profile render: identity as the
 * API resolves it (their account's, else KIPU's), program details from the profile, the contact
 * status derived, and blanks as unset.
 */
export const toClientRecord = (client: ClientFieldsFragment): ClientRecord => {
    const { assignedCoordinator: coordinator } = client;
    const coordinatorName = [
        coordinator?.firstName,
        coordinator?.lastName,
    ]
        .filter(Boolean)
        .join(" ");
    return {
        id: client.id,
        firstName: client.firstName ?? "",
        lastName: client.lastName ?? "",
        phone: present(client.phoneNumber),
        email: present(client.email),
        location: present(client.location),
        program: present(client.program),
        coordinator: present(coordinatorName || coordinator?.email),
        ...mockLifeStatusFor(client.email ?? ""),
        address: present(client.address),
        sex: present(client.sex),
        genderIdentity: present(client.genderIdentity),
        dischargeType: client.dischargeType ?? undefined,
        soberDate: present(client.soberDate),
        dischargeDate: present(client.dischargeDate),
        birthday: present(client.birthday),
        // A stopped client shows the coordinator's decision; an ACTIVE one, how engaged they are.
        status:
            client.outreachStatus === "ACTIVE" && client.engagementStatus
                ? client.engagementStatus
                : CONTACT_STATUS_FOR_OUTREACH_STATUS[client.outreachStatus],
        statusReason: present(client.outreachStatusReason),
        doNotContactReason: client.doNotContactReason ?? undefined,
        welcomeEmailSentAt: client.welcomeEmailSentAt ? String(client.welcomeEmailSentAt) : undefined,
        welcomeEmailBlocker: client.welcomeEmailBlocker ?? undefined,
        lostContactSuggested: client.lostContactSuggested,
        // status: CONTACT_STATUS_FOR_OUTREACH_STATUS[client.outreachStatus],
        emergencyContact: client.emergencyContactName
            ? {
                  name: client.emergencyContactName,
                  relationship: present(client.emergencyContactRelationship),
                  phone: present(client.emergencyContactPhone),
              }
            : undefined,
        contacts: [],
    };
};

/** How many of the newest notes the profile shows; the rest are in the notes full view. */
export const NOTES_PREVIEW_COUNT = 3;

/** The API refuses a longer note, so the note fields stop there rather than failing on save. */
export const NOTE_MAX_LENGTH = 5000;

/** The same for the reason given when outreach stops (`CLIENT_OUTREACH_STATUS_REASON_MAX_LENGTH`). */
export const STATUS_REASON_MAX_LENGTH = 500;

/** A note as the notes card shows it. Notes load on their own query, not as part of the alum. */
export const toCoordinatorNote = (note: CoordinatorNoteFieldsFragment): CoordinatorNote => {
    const name = [
        note.author?.firstName,
        note.author?.lastName,
    ]
        .filter(Boolean)
        .join(" ");
    return {
        id: note.id,
        text: note.body,
        author: name || note.author?.email || null,
        canEdit: note.viewerCanEdit,
        createdAt: note.createdAt,
        ...(new Date(note.updatedAt) > new Date(note.createdAt) && { editedAt: note.updatedAt }),
    };
};

/** One logged contact from the API, as the contact history shows it. */
export const toContactEntry = (contact: ClientContactFieldsFragment): ContactEntry => {
    const by = contact.loggedBy;
    return {
        id: contact.id,
        channel: CHANNEL_FOR_CONTACT_TYPE[contact.type],
        outcome: OUTCOME_FOR_API_OUTCOME[contact.outcome],
        contactedAt: String(contact.contactedAt),
        loggedAt: String(contact.createdAt),
        canEdit: contact.viewerCanEdit,
        note: present(contact.notes),
        followUpDate: present(contact.followUpDate),
        contactedBy: by
            ? [
                  by.firstName,
                  by.lastName,
              ]
                  .filter(Boolean)
                  .join(" ") || by.email
            : "",
    };
};
